import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import Client from '../../lib/client';

// Initialize API client
const apiClient = new Client('web', 'v2');

// Types for the new API structure
export interface GlossaryTerm {
  slug: string;
  name: string;
  title: string;
  letter: string;
  summary: string;
  content: string;
  modified?: string;
  published?: string;
  status?: string;
  // Additional fields from the API
  type?: string;
  subtype?: string;
  shortcode?: string;
  order?: number;
  cover?: {
    uri: string;
    name: string;
    extension: string;
    mime_type: string;
    alt: string;
    width: number;
    height: number;
  };
  subject?: {
    slug: string;
    name: string;
    title: string;
  };
  format?: {
    slug: string;
    name: string;
    type: string;
    subtype: string;
  };
  grades?: string;
  release?: string;
  review_tool?: any;
  gateways?: any[];
  details?: any[];
  groups?: string[];
  alerts?: any[];
  copyright?: string;
  options?: any[];
  multiple?: boolean;
  sequential?: boolean;
  required?: boolean;
  disabled?: boolean;
  description?: string;
}

export interface GlossaryLetterGroup {
  letter: string;
  terms: Array<{
    slug: string;
    title: string;
    summary: string;
    content: string;
    modified?: string;
    published?: string;
    status?: string;
  }>;
}

export interface GlossaryResponse {
  status: string;
  message: string;
  total: number;
  items: GlossaryLetterGroup[];
}

export interface GlossaryRequestParams {
  letter?: string;
  limit?: number;
  offset?: number;
  order?: 'alpha' | 'date';
  direction?: 'asc' | 'desc';
  search?: string;
  exact?: boolean; // New parameter for exact term matching
}

export const useGlossaryStore = defineStore('glossary', () => {
  const isOpen = ref(false);
  const currentTerm = ref<string | null>(null);
  const glossaryData = ref<GlossaryTerm[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const total = ref(0);

  // Caching state
  const cache = ref<
    Map<string, { data: GlossaryTerm[]; total: number; timestamp: number }>
  >(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Search and filter state
  const searchQuery = ref('');
  const selectedLetter = ref<string | null>(null);
  const sortOrder = ref<'alpha' | 'date'>('alpha');
  const sortDirection = ref<'asc' | 'desc'>('asc');
  const currentLimit = ref(50);
  const currentOffset = ref(0);

  // State to track if we're showing prioritized results
  const isPrioritizedSearch = ref(false);

  // Computed properties
  const groupedGlossaryItems = computed(() => {
    const grouped: { [key: string]: GlossaryTerm[] } = {};

    if (glossaryData.value && Array.isArray(glossaryData.value)) {
      // Always group by letter, even if there are exact matches
      glossaryData.value.forEach((item) => {
        const letter = item.letter.toUpperCase();
        if (!grouped[letter]) {
          grouped[letter] = [];
        }
        grouped[letter].push(item);
      });

      // Sort items within each letter group based on sort direction
      Object.keys(grouped).forEach((letter) => {
        grouped[letter].sort((a, b) => {
          const nameA = (a.name || a.title || '').toLowerCase();
          const nameB = (b.name || b.title || '').toLowerCase();

          if (sortDirection.value === 'desc') {
            return nameB.localeCompare(nameA); // Z-A
          } else {
            return nameA.localeCompare(nameB); // A-Z
          }
        });
      });
    }

    return grouped;
  });

  const sortedLetters = computed(() => {
    const letters = Object.keys(groupedGlossaryItems.value);

    // If "Exact Matches" exists, it should always be first
    if (letters.includes('Exact Matches')) {
      const exactMatches = ['Exact Matches'];
      const otherLetters = letters.filter(
        (letter) => letter !== 'Exact Matches',
      );

      if (sortDirection.value === 'desc') {
        return [...exactMatches, ...otherLetters.sort().reverse()];
      } else {
        return [...exactMatches, ...otherLetters.sort()];
      }
    }

    // Normal sorting when no exact matches
    if (sortDirection.value === 'desc') {
      return letters.sort().reverse(); // Z-A: Letter sections reversed (Z, Y, X, W...)
    } else {
      return letters.sort(); // A-Z: Letter sections normal (A, B, C, D...)
    }
  });

  // API endpoint
  const API_ENDPOINT = '_ah/api/web/v2/pages/glossary';

  // Actions
  // Helper function to generate cache key
  function generateCacheKey(params: GlossaryRequestParams): string {
    const key = {
      letter: params.letter || selectedLetter.value,
      limit: params.limit || currentLimit.value,
      offset: params.offset || currentOffset.value,
      order: params.order || sortOrder.value,
      direction: params.direction || sortDirection.value,
      search: params.search || searchQuery.value,
      exact: params.exact || false,
    };
    return JSON.stringify(key);
  }

  // Helper function to check if cache is valid
  function isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < CACHE_DURATION;
  }

  async function fetchGlossaryData(params: GlossaryRequestParams = {}) {
    loading.value = true;
    error.value = null;

    try {
      const requestPayload = {
        letter: params.letter || selectedLetter.value,
        limit: params.limit || currentLimit.value,
        offset: params.offset || currentOffset.value,
        order: params.order || sortOrder.value,
        direction: params.direction || sortDirection.value,
        search: params.search || searchQuery.value,
        exact: params.exact || false,
      };

      // Remove undefined values
      const cleanedPayload = Object.fromEntries(
        Object.entries(requestPayload).filter(
          ([_, value]) => value !== undefined && value !== null && value !== '',
        ),
      );

      // Generate cache key
      const cacheKey = generateCacheKey(params);

      // Check cache first
      const cachedData = cache.value.get(cacheKey);
      if (cachedData && isCacheValid(cachedData.timestamp)) {
        // Use cached data
        glossaryData.value = cachedData.data;
        total.value = cachedData.total;
        loading.value = false;
        return;
      }

      // Make API call
      const response = (await apiClient.execute(
        API_ENDPOINT,
        cleanedPayload,
        'POST',
      )) as GlossaryResponse;

      if (response.status === 'success') {
        const letterGroups = response.items || [];
        const totalCount = response.total || 0;

        // Flatten the structure: extract all terms from all letter groups
        const data: GlossaryTerm[] = [];
        letterGroups.forEach((letterGroup: GlossaryLetterGroup) => {
          if (letterGroup.terms && Array.isArray(letterGroup.terms)) {
            letterGroup.terms.forEach((term) => {
              // Add the letter property to each term for grouping
              data.push({
                ...term,
                letter: letterGroup.letter,
                // Use title as name for consistency
                name: term.title,
              });
            });
          }
        });

        console.log('🌐 Flattened data:', data.length, 'terms');

        // If we have no items but API says there's a total, adjust the total
        if (data.length === 0 && totalCount > 0) {
          total.value = 0;
        } else {
          total.value = totalCount;
        }

        // Update state
        glossaryData.value = data;

        // Cache the response
        cache.value.set(cacheKey, {
          data,
          total: totalCount,
          timestamp: Date.now(),
        });
      } else {
        throw new Error(response.message || 'Failed to fetch glossary data');
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to fetch glossary data';
      console.error('Error fetching glossary data:', err);
    } finally {
      loading.value = false;
    }
  }

  function openGlossary(term?: string) {
    console.log('🚀 openGlossary called with term:', term);

    if (term) {
      currentTerm.value = term;
      // Set the search query to the term so the UI shows the search
      searchQuery.value = term;
      // First try exact term match, then fall back to search
      searchForTermWithPriority(term);
    } else {
      // Clear search state for general browsing
      isPrioritizedSearch.value = false;
      searchQuery.value = '';
      currentTerm.value = null;
      // Load initial data for general browsing
      fetchGlossaryData();
    }
    isOpen.value = true;
  }

  function closeGlossary() {
    isOpen.value = false;
    currentTerm.value = null;
  }

  // Function that prioritizes exact term matches (now just calls searchGlossary for consistency)
  async function searchForTermWithPriority(term: string) {
    console.log('🔍 searchForTermWithPriority called with term:', term);

    // Set flag to indicate this is a prioritized search
    isPrioritizedSearch.value = true;

    // Use the same logic as searchGlossary for consistency
    await searchGlossary(term);
  }

  async function searchForTerm(term: string) {
    await fetchGlossaryData({ search: term, limit: 10 });
  }

  async function filterByLetter(letter: string) {
    selectedLetter.value = letter;
    currentOffset.value = 0; // Reset offset when changing letter
    isPrioritizedSearch.value = false; // Reset prioritized search flag
    await fetchGlossaryData({ letter });
  }

  async function searchGlossary(query: string) {
    searchQuery.value = query;
    selectedLetter.value = null; // Clear letter filter when searching
    currentOffset.value = 0; // Reset offset when searching
    isPrioritizedSearch.value = false; // Reset prioritized search flag

    // Use the same logic as searchForTermWithPriority for consistency
    await fetchGlossaryData({ search: query, limit: 50, exact: true });

    // Apply the same sorting logic for exact matches
    if (glossaryData.value && glossaryData.value.length > 0) {
      const termLower = query.toLowerCase();
      const exactMatches: GlossaryTerm[] = [];
      const partialMatches: GlossaryTerm[] = [];

      // Separate exact matches from partial matches
      glossaryData.value.forEach((item) => {
        const itemName = (item.name || item.title || '').toLowerCase();
        const itemSlug = (item.slug || '').toLowerCase();

        // Check for exact matches (slug or name)
        if (itemSlug === termLower || itemName === termLower) {
          exactMatches.push(item);
        } else {
          partialMatches.push(item);
        }
      });

      // Sort exact matches (should be few, but just in case)
      exactMatches.sort((a, b) => {
        const aName = (a.name || a.title || '').toLowerCase();
        const bName = (b.name || b.title || '').toLowerCase();
        return aName.localeCompare(bName);
      });

      // Sort partial matches by relevance
      partialMatches.sort((a, b) => {
        const aName = (a.name || a.title || '').toLowerCase();
        const bName = (b.name || b.title || '').toLowerCase();

        // Priority 1: Starts with term
        const aStartsWith = aName.startsWith(termLower);
        const bStartsWith = bName.startsWith(termLower);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;

        // Priority 2: Contains term (closer to beginning is better)
        const aIndex = aName.indexOf(termLower);
        const bIndex = bName.indexOf(termLower);
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1 && bIndex === -1) return -1;
        if (bIndex !== -1 && aIndex === -1) return 1;

        // Default: alphabetical
        return aName.localeCompare(bName);
      });

      // Combine exact matches first, then partial matches
      glossaryData.value = [...exactMatches, ...partialMatches];

      // Set current term to the first exact match if found
      if (exactMatches.length > 0) {
        currentTerm.value = exactMatches[0].slug;
      }
    }
  }

  async function changeSortOrder(
    order: 'alpha' | 'date',
    direction: 'asc' | 'desc' = 'asc',
  ) {
    sortOrder.value = order;
    sortDirection.value = direction;

    // Check if we have existing data to sort client-side
    if (glossaryData.value && glossaryData.value.length > 0) {
      // Just update the reactive values - computed properties will handle the rest
      // No need to trigger data changes, just let reactivity work
    } else {
      await fetchGlossaryData({ order, direction });
    }
  }

  async function loadMoreData() {
    if (loading.value || glossaryData.value.length >= total.value) {
      return;
    }

    currentOffset.value += currentLimit.value;
    const params: GlossaryRequestParams = {
      offset: currentOffset.value,
      limit: currentLimit.value,
    };

    // Add current filters to load more request
    if (selectedLetter.value) params.letter = selectedLetter.value;
    if (searchQuery.value) params.search = searchQuery.value;

    loading.value = true;

    try {
      const response = (await apiClient.execute(
        API_ENDPOINT,
        params,
        'POST',
      )) as GlossaryResponse;

      if (response.status === 'success') {
        const letterGroups = response.items || [];

        // If no new items returned or items is null, we've reached the end
        if (letterGroups.length === 0 || response.items === null) {
          // Update total to current length to prevent further load more attempts
          total.value = glossaryData.value?.length || 0;
          return;
        }

        // Flatten the structure: extract all terms from all letter groups
        const newItems: GlossaryTerm[] = [];
        letterGroups.forEach((letterGroup: GlossaryLetterGroup) => {
          if (letterGroup.terms && Array.isArray(letterGroup.terms)) {
            letterGroup.terms.forEach((term) => {
              // Add the letter property to each term for grouping
              newItems.push({
                ...term,
                letter: letterGroup.letter,
                // Use title as name for consistency
                name: term.title,
              });
            });
          }
        });

        // Append new items to existing data
        glossaryData.value = [...(glossaryData.value || []), ...newItems];

        // Update total, but ensure it's not less than current data length
        const newTotal = response.total || 0;
        const currentLength = glossaryData.value.length;
        total.value = Math.max(newTotal, currentLength);
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load more data';
      console.error('Error loading more glossary data:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearFilters() {
    searchQuery.value = '';
    selectedLetter.value = null;
    currentOffset.value = 0;
    isPrioritizedSearch.value = false; // Reset prioritized search flag
    fetchGlossaryData();
  }

  // Initialize data on first load
  function initializeGlossary() {
    if ((glossaryData.value?.length || 0) === 0) {
      fetchGlossaryData();
    }
  }

  // Clear cache (useful for testing or when needed)
  function clearCache() {
    cache.value.clear();
  }

  // Preload common terms for better performance
  function preloadCommonTerms() {
    const commonTerms = [
      'alignment',
      'standards',
      'assessment',
      'criteria',
      'gateway',
    ];
    commonTerms.forEach((term) => {
      searchForTerm(term);
    });
  }

  return {
    // State
    isOpen,
    currentTerm,
    glossaryData,
    loading,
    error,
    total,
    searchQuery,
    selectedLetter,
    sortOrder,
    sortDirection,
    isPrioritizedSearch,

    // Computed
    groupedGlossaryItems,
    sortedLetters,

    // Actions
    openGlossary,
    closeGlossary,
    fetchGlossaryData,
    searchForTerm,
    searchForTermWithPriority,
    filterByLetter,
    searchGlossary,
    changeSortOrder,
    loadMoreData,
    clearFilters,
    initializeGlossary,
    clearCache,
    preloadCommonTerms,
  };
});
